import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  GlobeAltIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const PFIContactSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    companyName: 'Pink Flamingo Interiors',
    primaryContact: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    projectsEmail: '',
    suppliersEmail: '',
    adminPhone: ''
  });

  useEffect(() => {
    loadContactInfo();
  }, []);

  const loadContactInfo = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'settings', 'pfiContact');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setContactInfo({
          ...contactInfo,
          ...docSnap.data()
        });
      }
    } catch (error) {
      console.error('Error loading contact info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setSaved(false);
      
      const docRef = doc(db, 'settings', 'pfiContact');
      await setDoc(docRef, {
        ...contactInfo,
        updatedAt: serverTimestamp()
      });
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving contact info:', error);
      alert('Failed to save contact information: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContactInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
        <span className="ml-2 text-gray-600">Loading contact settings...</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <BuildingOfficeIcon className="w-6 h-6 text-pink-600 mr-3" />
          <h2 className="text-xl font-bold text-gray-900">Pink Flamingo Contact Information</h2>
        </div>
        {saved && (
          <div className="flex items-center text-green-600">
            <CheckCircleIcon className="w-5 h-5 mr-2" />
            <span className="text-sm font-medium">Saved successfully!</span>
          </div>
        )}
      </div>
      
      <p className="text-gray-600 mb-6">
        Configure your company contact information that appears in emails, job sheets, and supplier communications.
      </p>

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name *
            </label>
            <input
              type="text"
              value={contactInfo.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              required
            />
          </div>

          {/* Primary Contact Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Contact Name *
            </label>
            <input
              type="text"
              value={contactInfo.primaryContact}
              onChange={(e) => handleChange('primaryContact', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              placeholder="e.g., Christine Smith"
              required
            />
          </div>

          {/* Main Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Main Phone Number *
            </label>
            <div className="relative">
              <PhoneIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={contactInfo.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="e.g., +61 400 000 000"
                required
              />
            </div>
          </div>

          {/* Main Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Main Email Address *
            </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={contactInfo.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="e.g., info@pinkflamingo.com.au"
                required
              />
            </div>
          </div>

          {/* Projects Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Projects Email
              <span className="text-xs text-gray-500 ml-2">(for project inquiries)</span>
            </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={contactInfo.projectsEmail}
                onChange={(e) => handleChange('projectsEmail', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="e.g., projects@pinkflamingo.com.au"
              />
            </div>
          </div>

          {/* Suppliers Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Suppliers Email
              <span className="text-xs text-gray-500 ml-2">(for supplier communications)</span>
            </label>
            <div className="relative">
              <EnvelopeIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={contactInfo.suppliersEmail}
                onChange={(e) => handleChange('suppliersEmail', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="e.g., suppliers@pinkflamingo.com.au"
              />
            </div>
          </div>

          {/* Admin Phone (for SMS notifications) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Phone
              <span className="text-xs text-gray-500 ml-2">(receives SMS notifications)</span>
            </label>
            <div className="relative">
              <PhoneIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={contactInfo.adminPhone}
                onChange={(e) => handleChange('adminPhone', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="e.g., +61 400 000 000"
              />
            </div>
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <div className="relative">
              <GlobeAltIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={contactInfo.website}
                onChange={(e) => handleChange('website', e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="e.g., https://www.pinkflamingo.com.au"
              />
            </div>
          </div>
        </div>

        {/* Full Address */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Address
          </label>
          <div className="relative">
            <MapPinIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <textarea
              value={contactInfo.address}
              onChange={(e) => handleChange('address', e.target.value)}
              rows={2}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              placeholder="e.g., 123 Design Street, Sydney NSW 2000, Australia"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              'Save Contact Information'
            )}
          </button>
        </div>
      </form>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Where This Information Is Used:</h3>
        <div className="text-blue-800 text-sm space-y-1">
          <p>• Email footers in quote invitations and job sheets</p>
          <p>• Contact details on supplier portal pages</p>
          <p>• Reply-to addresses in automated emails</p>
          <p>• SMS notifications (admin phone receives quote submissions)</p>
          <p>• Job sheet acknowledgment notifications</p>
        </div>
      </div>
    </div>
  );
};

export default PFIContactSettings;